/**
 * Reads the six operands of a DICT's `FontMatrix` the way `cff_parse_font_matrix` (cffparse.c) does.
 * It keeps every operand as a 16.16 number times a power of ten, so that a matrix of small reals
 * such as 0.001 keeps its digits. It then brings the six to one power of ten, which becomes the
 * units per em of the matrix.
 *
 * Portions ported from FreeType (FreeType Project License).
 * Copyright (C) 1996-2026 by David Turner, Robert Wilhelm, and Werner Lemberg.
 * See THIRD-PARTY-NOTICES.md.
 */
import { identityFontMatrix } from './cffFontMatrix';
import { readRealDigits } from './cffReal';
import { FIXED_16_16_ONE, ftDivFix, ftMulDiv } from './fontMetrics';
/** The largest integer part of a 16.16 number. */
const MAX_FIXED_INTEGER = 0x7fff;
/** The powers of ten that `cff_parse_font_matrix` accepts for its largest operand, and the spread between the largest and the smallest. */
const MIN_LARGEST_SCALING = -9;
const MAX_SCALING_SPREAD = 9;
const REAL_OPERATOR = 30;
const ZERO_NUMBER = { value: 0, scaling: 0 };
const powerOfTen = (exponent) => 10 ** exponent;
const signed = (magnitude, isNegative) => (isNegative ? -magnitude : magnitude);
/** `cff_parse_real` with a scaling: the real whose operator `30` is at `start`, as 16.16 and the power of ten left over. */
function parseScaledReal(dv, start, limit) {
    const digits = readRealDigits(dv, start, limit);
    if (!digits || !digits.number)
        return ZERO_NUMBER;
    if (digits.overflow !== 'none') {
        return { value: digits.overflow === 'small' ? 0 : signed(0x7fffffff, digits.isNegative), scaling: 0 };
    }
    return scaledReal(digits.number, digits.exponent + digits.integerLength, digits.fractionLength + digits.integerLength, digits.isNegative);
}
/** The digits `number`, with `fractionLength` of them after the point and a power of ten of `exponent`, as 16.16 and a power of ten. */
function scaledReal(number, exponent, fractionLength, isNegative) {
    if (fractionLength > 5) {
        const excess = fractionLength - 5;
        const isTooLarge = Math.trunc(number / powerOfTen(excess)) > MAX_FIXED_INTEGER;
        const digitsDropped = isTooLarge ? excess + 1 : excess;
        return {
            value: signed(ftDivFix(number, powerOfTen(digitsDropped)), isNegative),
            scaling: exponent - 5 + (isTooLarge ? 1 : 0),
        };
    }
    if (number > MAX_FIXED_INTEGER) {
        return { value: signed(ftDivFix(number, 10), isNegative), scaling: exponent - fractionLength + 1 };
    }
    // Make the power of ten as small as possible: move up to five digits of it into the number.
    const shift = Math.min(exponent, 5) - fractionLength;
    if (shift <= 0) {
        return { value: signed(number * FIXED_16_16_ONE, isNegative), scaling: exponent - fractionLength };
    }
    const shifted = number * powerOfTen(shift);
    const isTooLargeNow = shifted > MAX_FIXED_INTEGER;
    return {
        value: signed((isTooLargeNow ? Math.trunc(shifted / 10) : shifted) * FIXED_16_16_ONE, isNegative),
        scaling: exponent - Math.min(exponent, 5) + (isTooLargeNow ? 1 : 0),
    };
}
/** The integer `operand` of a DICT as 16.16 and the power of ten left over (`cff_parse_fixed_dynamic`). */
function scaledInteger(operand) {
    if (operand <= MAX_FIXED_INTEGER)
        return { value: operand * FIXED_16_16_ONE, scaling: 0 };
    let integerLength = 5;
    while (integerLength < 10 && operand >= powerOfTen(integerLength))
        integerLength++;
    const isTooLarge = Math.trunc(operand / powerOfTen(integerLength - 5)) > MAX_FIXED_INTEGER;
    const digitsDropped = isTooLarge ? integerLength - 4 : integerLength - 5;
    return { value: ftDivFix(operand, powerOfTen(digitsDropped)), scaling: digitsDropped };
}
/** `FT_Matrix_Check`: whether the matrix is not degenerate, a test on its size and its determinant. */
function isUsableMatrix(xx, xy, yx, yy) {
    const largest = Math.max(Math.abs(xx), Math.abs(xy), Math.abs(yx), Math.abs(yy));
    if (!largest || largest > 0x7fffffff)
        return false;
    const shift = 31 - Math.clz32(largest) - 12;
    if (shift > 0) {
        xx >>= shift;
        xy >>= shift;
        yx >>= shift;
        yy >>= shift;
    }
    return 32 * Math.abs(xx * yy - xy * yx) > xx * xx + xy * xy + yx * yx + yy * yy;
}
/**
 * The `FontMatrix` whose `operands` start at `operandStarts` in `dv`, as written in the DICT, or
 * null for fewer than six operands. FreeType falls back to the identity, with one unit per em,
 * for operands that differ by more than nine powers of ten, exceed 16.16, or have no determinant.
 */
export function readFontMatrix(dv, operands, operandStarts, limit) {
    if (operands.length < 6)
        return null;
    const numbers = operands
        .slice(0, 6)
        .map((operand, i) => dv.getUint8(operandStarts[i]) === REAL_OPERATOR
        ? parseScaledReal(dv, operandStarts[i], limit)
        : scaledInteger(operand));
    const scalings = numbers.filter(({ value }) => value).map(({ scaling }) => scaling);
    if (scalings.length === 0)
        return identityFontMatrix(1);
    const maxScaling = Math.max(...scalings);
    if (maxScaling < MIN_LARGEST_SCALING ||
        maxScaling > 0 ||
        maxScaling - Math.min(...scalings) > MAX_SCALING_SPREAD) {
        return identityFontMatrix(1);
    }
    const [xx, yx, xy, yy, offsetX, offsetY] = numbers.map(({ value, scaling }) => ftMulDiv(value, 1, powerOfTen(maxScaling - scaling)));
    return isUsableMatrix(xx, xy, yx, yy)
        ? { xx, yx, xy, yy, offsetX, offsetY, unitsPerEm: powerOfTen(-maxScaling) }
        : identityFontMatrix(1);
}
//# sourceMappingURL=cffFontMatrixOperands.js.map